import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../base/Button';

interface ReviewSubmissionProps {
  onSubmit?: (review: {
    rating: number;
    comment: string;
    name: string;
  }) => void;
}

export default function ReviewSubmission({ onSubmit }: ReviewSubmissionProps) {
  const { t } = useTranslation(['timer', 'common']);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStarClick = (value: number) => {
    setRating(value);
    setError('');
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (rating === 0) {
      setError(t('reviewSubmission.error'));
      return;
    }

    setIsSubmitting(true);

    // 模拟提交评价
    const reviewData = {
      rating,
      comment: comment.trim(),
      name: name.trim() || 'Anonymous User',
    };

    // 调用回调函数
    onSubmit?.(reviewData);

    // 显示简短的成功提示
    setTimeout(() => {
      setIsSubmitting(false);
      setRating(0);
      setComment('');
      setName('');
      setError('');
    }, 1000);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
      <div className="text-center mb-6">
        <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2">{t('reviewSubmission.title')}</h3>
        <p className="text-gray-600 text-sm md:text-base">{t('reviewSubmission.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-md mx-auto">
        {/* 星级评价 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('reviewSubmission.ratingLabel')}
          </label>
          <div className="flex justify-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleStarClick(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="text-3xl transition-colors focus:outline-none"
              >
                <i
                  className={`${
                    star <= (hoveredRating || rating)
                      ? 'ri-star-fill text-yellow-400'
                      : 'ri-star-line text-gray-300'
                  } hover:text-yellow-400`}
                ></i>
              </button>
            ))}
          </div>
          {error && (
            <p className="text-red-500 text-sm mt-2 text-center">{error}</p>
          )}
        </div>

        {/* 评价内容 */}
        <div className="mb-4">
          <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
            {t('reviewSubmission.commentLabel')}
          </label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('reviewSubmission.commentPlaceholder')}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
            maxLength={500}
          />
          <div className="text-xs text-gray-500 mt-1 text-right">
            {comment.length}/500
          </div>
        </div>

        {/* 用户名 */}
        <div className="mb-6">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            {t('reviewSubmission.nameLabel')}
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('reviewSubmission.namePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            maxLength={50}
          />
        </div>

        {/* 提交按钮 */}
        <Button
          className="w-full"
          disabled={rating === 0 || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line mr-2 animate-spin"></i>
              {t('common:button.submitting', 'Submitting...')}
            </>
          ) : (
            <>
              <i className="ri-send-plane-line mr-2"></i>
              {t('reviewSubmission.submitButton')}
            </>
          )}
        </Button>
      </form>
    </div>
  );
}